import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import PageMasthead from '../../components/PageMasthead'
import Spinner from '../../components/Spinner'
import ConfirmDialog from '../../components/ConfirmDialog'
import ClassifierDeploymentSection from './ClassifierDeploymentSection'
import ClassifierLlmClassificationWarning from './ClassifierLlmClassificationWarning'
import ClassifierArchiveCard from './classifier/ClassifierArchiveCard'
import ClassifierDetailsForm from './classifier/ClassifierDetailsForm'
import ClassifierVersionList from './classifier/ClassifierVersionList'
import ClassifierVersionPanel, { DraftFormValues } from './classifier/ClassifierVersionPanel'
import {
    findDraft,
    findLive,
    pickDefaultVersion,
    versionSubtitle
} from './classifier/classifierVersions'
import { useClassifierEditActions } from './classifier/useClassifierEditActions'
import {
    ClassifierPromptStatus,
    useGetClassifierDeployedProfilesWithoutLlmClassificationQuery,
    useGetClassifierPromptVersionsQuery,
    useGetManagedClassifierDetailsQuery
} from '../../api/client'

export default function ClassifierEditPage() {
    const { id: idParam } = useParams<{ id: string }>()
    const id = Number(idParam)

    // cache-and-network so a just-created classifier (and its versions) is always
    // revalidated — otherwise a cache-first hit on the managed list can miss it and
    // fall through to "Classifier not found".
    const detailsQuery = useGetManagedClassifierDetailsQuery({ fetchPolicy: 'cache-and-network' })
    const versionsQuery = useGetClassifierPromptVersionsQuery({
        variables: { classifierDetailsId: id },
        skip: Number.isNaN(id),
        fetchPolicy: 'cache-and-network'
    })

    // Profiles the prompt is applied to but can't run on. Refetched whenever the
    // deployments change or a switch is flipped from the warning itself.
    const switchedOffQuery = useGetClassifierDeployedProfilesWithoutLlmClassificationQuery({
        variables: { classifierDetailsId: id },
        skip: Number.isNaN(id),
        fetchPolicy: 'cache-and-network'
    })
    const switchedOffProfiles =
        switchedOffQuery.data?.getClassifierDeployedProfilesWithoutLlmClassification ?? []

    const detail = detailsQuery.data?.getManagedClassifierDetails.find((d) => d.id === id)
    const versions = versionsQuery.data?.getClassifierPromptVersions ?? []

    // Selection is an override rather than the source of truth: with nothing picked, or
    // a pick that's since been discarded, the shown version falls out of the list.
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const selected = versions.find((v) => v.id === selectedId) ?? pickDefaultVersion(versions)

    const draft = findDraft(versions)
    const live = findLive(versions)
    const isArchived = selected?.status === ClassifierPromptStatus.Archived

    const [unsavedValues, setUnsavedValues] = useState<DraftFormValues | null>(null)
    const [modal, setModal] = useState<'none' | 'golive' | 'archive'>('none')

    const actions = useClassifierEditActions({
        classifierDetailsId: id,
        refetchDetails: detailsQuery.refetch,
        refetchVersions: versionsQuery.refetch
    })

    async function handleNewDraft() {
        const createdId = await actions.createDraft(versions[0])
        if (createdId != null) setSelectedId(createdId)
    }

    async function handleDiscardDraft() {
        if (!draft) return
        if (await actions.discardDraft(draft.id)) setSelectedId(null)
    }

    async function handleGoLive() {
        if (!selected) return
        // Going live with a stale draft would publish something other than what's on
        // screen, so persist edits first and stop if that fails (e.g. invalid thresholds).
        if (unsavedValues && !(await actions.saveDraft(selected.id, unsavedValues))) {
            setModal('none')
            return
        }
        await actions.goLive(selected.id)
        setModal('none')
    }

    async function handleArchive() {
        await actions.archive()
        setModal('none')
    }

    if (Number.isNaN(id)) {
        return <div className="p-6 text-sm text-severe">Invalid classifier id.</div>
    }

    // Until we have the classifier, show a spinner while any query is still in
    // flight (including cache-and-network revalidation), and only "not found" once
    // everything has settled without it. With cached data present, `detail` is set
    // and we render straight away.
    if (!detail) {
        if (detailsQuery.loading || versionsQuery.loading) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-ink-50">
                    <Spinner size={6} />
                </div>
            )
        }
        return (
            <div className="min-h-screen bg-ink-50 p-6">
                <p className="text-sm text-ink-500">Classifier not found.</p>
                <Link
                    to="/admin/classifiers"
                    className="mt-2 inline-block text-sm text-arwen-600 hover:text-arwen-800"
                >
                    ← Back to classifiers
                </Link>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-ink-50">
            <PageMasthead
                title={detail.displayName}
                tabTitle={detail.displayName}
                subtitle={selected ? versionSubtitle(selected) : undefined}
            />

            <div className="mx-auto max-w-5xl space-y-6 p-6">
                {detail.archived && (
                    <div className="rounded-lg border border-ink-200 bg-ink-100 px-4 py-2.5 text-sm text-ink-700">
                        This classifier is <strong>archived</strong> — applied to no profiles and
                        hidden from the active list. Unarchive it below to use it again.
                    </div>
                )}
                {actions.error && (
                    <div className="rounded-lg border border-severe/30 bg-severe/10 p-3 text-sm text-severe">
                        {actions.error}
                    </div>
                )}

                {/* Details — unversioned metadata (full width). Keyed so the form
                    reseeds on navigation to another classifier and never mid-edit. */}
                <ClassifierDetailsForm
                    key={detail.id}
                    detail={detail}
                    saving={actions.savingDetail}
                    onSave={actions.saveDetail}
                />

                {/* Versions list (left) + selected version panel (right), equal height */}
                <div className="grid gap-6 lg:grid-cols-[260px_1fr] lg:items-stretch">
                    <ClassifierVersionList
                        versions={versions}
                        selectedVersionId={selected?.id ?? null}
                        onSelect={setSelectedId}
                        canAddDraft={!draft}
                        addingDraft={actions.creatingDraft}
                        onNewDraft={handleNewDraft}
                    />

                    {selected ? (
                        <ClassifierVersionPanel
                            key={selected.id}
                            version={selected}
                            saving={actions.savingDraft}
                            discarding={actions.discarding}
                            onSave={(values) => actions.saveDraft(selected.id, values)}
                            onDiscard={handleDiscardDraft}
                            onRequestGoLive={(unsaved) => {
                                setUnsavedValues(unsaved)
                                setModal('golive')
                            }}
                        />
                    ) : (
                        <div className="flex items-center justify-center rounded-lg border border-dashed border-ink-200 bg-white p-8 text-sm text-ink-400">
                            No versions yet — add a draft to get started.
                        </div>
                    )}
                </div>

                <ClassifierLlmClassificationWarning
                    profiles={switchedOffProfiles}
                    checkError={switchedOffQuery.error?.message ?? null}
                    onChanged={() => switchedOffQuery.refetch()}
                />

                {/* Applied to — deployment (full width) */}
                <ClassifierDeploymentSection
                    classifierDetailsId={id}
                    hasLiveVersion={live != null}
                    onDeploymentsChanged={() => switchedOffQuery.refetch()}
                />

                <ClassifierArchiveCard
                    archived={detail.archived}
                    unarchiving={actions.unarchiving}
                    onUnarchive={actions.unarchive}
                    onRequestArchive={() => setModal('archive')}
                />
            </div>

            <ConfirmDialog
                open={modal === 'golive'}
                onClose={() => setModal('none')}
                onConfirm={handleGoLive}
                loading={actions.goingLive}
                title={
                    isArchived
                        ? `Roll back to v${selected?.version}`
                        : `Go live with v${selected?.version}`
                }
                confirmLabel={isArchived ? 'Roll back' : 'Go live'}
                body={
                    <>
                        This makes <strong>v{selected?.version}</strong> the live version
                        {live ? `, archiving the current live version (v${live.version}).` : '.'}
                    </>
                }
            />

            <ConfirmDialog
                open={modal === 'archive'}
                onClose={() => setModal('none')}
                onConfirm={handleArchive}
                loading={actions.archiving}
                danger
                title="Archive classifier"
                confirmLabel="Archive classifier"
                body={
                    <>
                        Archiving <strong>{detail.displayName}</strong> will{' '}
                        <strong>remove it from every profile</strong> (deleting all its moderation
                        boundaries) and take it out of use. You can unarchive it later, but its
                        deployments won&rsquo;t be restored — you&rsquo;d re-apply it.
                    </>
                }
            />
        </div>
    )
}
